// ── Shared payment utilities ──────────────────────────────────────────────────
// Used by: VerifyBillPage, BillDetailPage (and any future payment views)

import { CreditCard, Banknote, Globe, Layers } from 'lucide-react';

// ── Currency formatting ───────────────────────────────────────────────────────

/**
 * Format a numeric (or stringified numeric) amount as EGP currency.
 * Safely handles undefined, null, and string amounts from the API.
 */
export function formatPaymentCurrency(
  amount: number | string | undefined | null,
  lang: string
): string {
  const num = Number(amount ?? 0);
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'en-US', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(isNaN(num) ? 0 : num);
}

// ── Date formatting ───────────────────────────────────────────────────────────

/**
 * Format a date string into a localized, human-readable date+time.
 * Returns '—' for undefined, null, or invalid dates.
 */
export function formatPaymentDate(
  dateStr: string | undefined | null,
  lang: string,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(
    lang === 'ar' ? 'ar-EG' : 'en-US',
    options ?? {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }
  );
}

// ── Payment Method Icon ───────────────────────────────────────────────────────

interface PaymentMethodIconProps {
  method?: string;
  className?: string;
}

/**
 * Renders the appropriate icon for a given payment method string.
 * Handles all known variants (snake_case, kebab-case).
 */
export function PaymentMethodIcon({ method, className = 'h-4 w-4' }: PaymentMethodIconProps) {
  switch ((method ?? '').toLowerCase()) {
    case 'card':
    case 'credit_card':
    case 'debit_card':
    case 'visa':
    case 'mastercard':
      return <CreditCard className={className} />;
    case 'online':
    case 'fawry':
    case 'paymob':
    case 'stripe':
      return <Globe className={className} />;
    case 'bank-transfer':
    case 'bank_transfer':
      return <Layers className={className} />;
    case 'e-wallet':
    case 'e_wallet':
    case 'cash':
    default:
      return <Banknote className={className} />;
  }
}

// ── Public base URL (for QR code generation) ─────────────────────────────────

/**
 * Determine the correct public-facing base URL for QR code generation.
 * Evaluated once at module load — not reactive to DOM changes.
 */
export const PUBLIC_PORTAL_BASE_URL = (() => {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  if (host.includes('routelms.com')) return 'https://routelms.com';
  if (host.includes('test.student.erp')) return 'https://test.student.erp.routeegypt.com';
  if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:4300';
  return window.location.origin;
})();
